import * as THREE from 'three';
import {
  ResourceTier,
  type WeaponVisualStyle,
  type ShieldVisualStyle,
} from '@viral-space/shared';

/**
 * Procedural item icons.
 *
 * Just like {@link ./ship-builder.ts} produces a distinct mesh per ship
 * class, this module produces a distinct mesh per inventory item kind
 * so the inventory / shop / drop callouts can show real 3D thumbnails
 * instead of a coloured dot.
 *
 * The current implementation focuses on **resources** — every other
 * item category (weapons, shields, engines, consumables) will plug
 * into the same `BuiltItem` interface and the same offscreen
 * thumbnail renderer in `ui/item-thumbnail.ts`.
 */

export interface BuiltItem {
  /**
   * Root group, sized so the bounding box is roughly a 1.2-unit cube
   * (matching the framing the thumbnail renderer expects).
   */
  group: THREE.Group;
  /**
   * Optional pulsing emissive materials (e.g. plasma cores). The
   * thumbnail renderer animates these like the ship-builder does for
   * thrusters when the thumbnail is rendered live.
   */
  pulses?: THREE.MeshStandardMaterial[];
}

// ─── Resource archetypes ──────────────────────────────────────────────
//
// Twenty-one resource tiers fall into a handful of recurring visual
// archetypes (rough ore, faceted crystal, plasma orb, exotic cloud, …).
// Building one geometry per archetype keeps the file readable and lets
// each tier express its identity through colour + small variations.

type ResourceArchetype =
  | 'ore'
  | 'crystal'
  | 'plasma'
  | 'bio'
  | 'cloud'
  | 'pearl'
  | 'silicate'
  | 'ingot';

const RESOURCE_ARCHETYPE: Record<ResourceTier, ResourceArchetype> = {
  // Core
  [ResourceTier.ScrapMetal]: 'ore',
  [ResourceTier.CrystalShards]: 'crystal',
  [ResourceTier.PlasmaCore]: 'plasma',
  [ResourceTier.VoidEssence]: 'plasma',
  [ResourceTier.DarkMatter]: 'cloud',
  [ResourceTier.QuantumFragment]: 'cloud',
  // Common rim
  [ResourceTier.IronOre]: 'ore',
  [ResourceTier.CopperOre]: 'ore',
  [ResourceTier.Silicate]: 'silicate',
  // Mid-range
  [ResourceTier.TitaniumAlloy]: 'ingot',
  [ResourceTier.SolarPlasma]: 'plasma',
  [ResourceTier.Bioflux]: 'bio',
  [ResourceTier.NeutroniumDust]: 'cloud',
  // Rare
  [ResourceTier.ChronoQuartz]: 'crystal',
  [ResourceTier.VoidPearl]: 'pearl',
  [ResourceTier.PhasePrism]: 'crystal',
  // Extreme
  [ResourceTier.StarforgeIngot]: 'ingot',
  [ResourceTier.EchoCrystal]: 'crystal',
  [ResourceTier.SingularityShard]: 'crystal',
  [ResourceTier.AbyssalTear]: 'plasma',
  [ResourceTier.EtherealEssence]: 'cloud',
  // Phase 3.1 — surface fauna drop. Read visually as bio-organic
  // material (consumed by Refinery crafting for biome-flavoured gear).
  [ResourceTier.OrganicMatter]: 'bio',
};

// ─── Helpers ──────────────────────────────────────────────────────────

function brighten(color: number, amount: number): number {
  const r = ((color >> 16) & 0xff) / 255;
  const g = ((color >> 8) & 0xff) / 255;
  const b = (color & 0xff) / 255;
  const t = Math.max(0, Math.min(1, amount));
  const nr = r + (1 - r) * t;
  const ng = g + (1 - g) * t;
  const nb = b + (1 - b) * t;
  return (
    (Math.round(nr * 255) << 16) |
    (Math.round(ng * 255) << 8) |
    Math.round(nb * 255)
  );
}

function darken(color: number, amount: number): number {
  const t = Math.max(0, Math.min(1, amount));
  const r = Math.round(((color >> 16) & 0xff) * (1 - t));
  const g = Math.round(((color >> 8) & 0xff) * (1 - t));
  const b = Math.round((color & 0xff) * (1 - t));
  return (r << 16) | (g << 8) | b;
}

function makeRockMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: darken(color, 0.1),
    roughness: 0.85,
    metalness: 0.15,
    flatShading: true,
  });
}

function makeMetalMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color,
    roughness: 0.35,
    metalness: 0.85,
    emissive: darken(color, 0.5),
    emissiveIntensity: 0.15,
    flatShading: true,
  });
}

function makeCrystalMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: brighten(color, 0.25),
    roughness: 0.15,
    metalness: 0.4,
    transparent: true,
    opacity: 0.85,
    emissive: color,
    emissiveIntensity: 0.5,
    flatShading: true,
  });
}

function makePlasmaMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    roughness: 0.25,
    metalness: 0.0,
    emissive: color,
    emissiveIntensity: 1.4,
    transparent: true,
    opacity: 0.92,
  });
}

function makeBioMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: brighten(color, 0.1),
    roughness: 0.5,
    metalness: 0.05,
    emissive: color,
    emissiveIntensity: 0.5,
  });
}

function makePearlMaterial(color: number): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    roughness: 0.1,
    metalness: 0.95,
    emissive: color,
    emissiveIntensity: 0.2,
  });
}

function perturb(geom: THREE.BufferGeometry, amount: number, seed: number): void {
  const pos = geom.attributes['position'] as THREE.BufferAttribute | undefined;
  if (!pos) return;
  let s = seed | 0;
  const rand = (): number => {
    // Mulberry32 — deterministic so the same tier always looks the same
    // on every render.
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < pos.count; i++) {
    pos.setX(i, pos.getX(i) + (rand() - 0.5) * amount);
    pos.setY(i, pos.getY(i) + (rand() - 0.5) * amount);
    pos.setZ(i, pos.getZ(i) + (rand() - 0.5) * amount);
  }
  pos.needsUpdate = true;
  geom.computeVertexNormals();
}

// ─── Per-archetype builders ───────────────────────────────────────────

function buildOre(color: number, seed: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makeRockMaterial(color);

  const main = new THREE.IcosahedronGeometry(0.55, 0);
  perturb(main, 0.18, seed);
  group.add(new THREE.Mesh(main, mat));

  const veinMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: brighten(color, 0.4),
    emissiveIntensity: 0.6,
    metalness: 0.6,
    roughness: 0.3,
  });
  for (let i = 0; i < 3; i++) {
    const vein = new THREE.Mesh(new THREE.SphereGeometry(0.12, 6, 6), veinMat);
    const a = (i / 3) * Math.PI * 2 + seed * 0.13;
    vein.position.set(
      Math.cos(a) * 0.45,
      Math.sin(a * 1.7) * 0.35,
      Math.sin(a) * 0.45,
    );
    group.add(vein);
  }
  return { group };
}

function buildIngot(color: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makeMetalMaterial(color);
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(1.0, 0.35, 0.55),
    mat,
  );
  // Trapezoid ingot: shrink the top face slightly.
  const pos = body.geometry.attributes['position'] as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    if (pos.getY(i) > 0) {
      pos.setX(i, pos.getX(i) * 0.85);
      pos.setZ(i, pos.getZ(i) * 0.8);
    }
  }
  pos.needsUpdate = true;
  body.geometry.computeVertexNormals();
  group.add(body);

  const wireGeom = new THREE.EdgesGeometry(body.geometry, 30);
  const wireMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.7),
    transparent: true,
    opacity: 0.55,
  });
  group.add(new THREE.LineSegments(wireGeom, wireMat));

  return { group };
}

function buildCrystal(color: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makeCrystalMaterial(color);

  // Stack two octahedra to make a faceted bipyramid, slightly elongated.
  const top = new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), mat);
  top.scale.set(0.7, 1.1, 0.7);
  group.add(top);
  group.add(new THREE.LineSegments(
    new THREE.EdgesGeometry(top.geometry, 1),
    new THREE.LineBasicMaterial({
      color: brighten(color, 0.6),
      transparent: true,
      opacity: 0.7,
    }),
  ));

  // Smaller offset shard.
  const shardMat = makeCrystalMaterial(brighten(color, 0.15));
  const shard = new THREE.Mesh(new THREE.OctahedronGeometry(0.22, 0), shardMat);
  shard.position.set(0.3, -0.2, 0.18);
  shard.scale.set(0.5, 0.9, 0.5);
  shard.rotation.set(0.3, 0.6, 0.2);
  group.add(shard);

  return { group, pulses: [mat] };
}

function buildPlasma(color: number): BuiltItem {
  const group = new THREE.Group();

  const containerMat = new THREE.MeshStandardMaterial({
    color: 0x222233,
    roughness: 0.4,
    metalness: 0.7,
    transparent: true,
    opacity: 0.55,
  });
  const shell = new THREE.Mesh(
    new THREE.IcosahedronGeometry(0.55, 1),
    containerMat,
  );
  group.add(shell);
  group.add(new THREE.LineSegments(
    new THREE.EdgesGeometry(shell.geometry, 1),
    new THREE.LineBasicMaterial({
      color: brighten(color, 0.4),
      transparent: true,
      opacity: 0.6,
    }),
  ));

  const coreMat = makePlasmaMaterial(color);
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.28, 16, 12), coreMat);
  group.add(core);

  return { group, pulses: [coreMat] };
}

function buildBio(color: number, seed: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makeBioMaterial(color);

  // Lumpy ovoid body.
  const body = new THREE.SphereGeometry(0.45, 12, 10);
  perturb(body, 0.1, seed);
  group.add(new THREE.Mesh(body, mat));

  // A few small spore buds.
  const budMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.3),
    emissive: color,
    emissiveIntensity: 1.0,
    roughness: 0.4,
  });
  for (let i = 0; i < 4; i++) {
    const bud = new THREE.Mesh(new THREE.SphereGeometry(0.09, 8, 8), budMat);
    const a = (i / 4) * Math.PI * 2 + seed * 0.2;
    bud.position.set(
      Math.cos(a) * 0.45,
      Math.sin(a * 1.3) * 0.3,
      Math.sin(a) * 0.45,
    );
    group.add(bud);
  }
  return { group, pulses: [budMat] };
}

function buildCloud(color: number, seed: number): BuiltItem {
  const group = new THREE.Group();

  // A cluster of overlapping translucent spheres reads as "fog / dust".
  const baseMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.3),
    transparent: true,
    opacity: 0.45,
    emissive: color,
    emissiveIntensity: 0.7,
    roughness: 0.9,
    depthWrite: false,
  });

  let s = seed | 0;
  const rand = (): number => {
    s = (s + 0x9e3779b9) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 16), 0x85ebca6b);
    t = Math.imul(t ^ (t >>> 13), 0xc2b2ae35);
    return ((t ^ (t >>> 16)) >>> 0) / 4294967296;
  };
  for (let i = 0; i < 6; i++) {
    const r = 0.18 + rand() * 0.18;
    const m = new THREE.Mesh(new THREE.SphereGeometry(r, 10, 8), baseMat);
    m.position.set(
      (rand() - 0.5) * 0.7,
      (rand() - 0.5) * 0.5,
      (rand() - 0.5) * 0.7,
    );
    group.add(m);
  }

  // Bright nucleus so the cloud has a centre of mass to read against.
  const coreMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.7),
    emissive: brighten(color, 0.5),
    emissiveIntensity: 1.6,
    transparent: true,
    opacity: 0.95,
  });
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 10), coreMat);
  group.add(core);

  return { group, pulses: [coreMat] };
}

function buildPearl(color: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makePearlMaterial(color);
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.5, 24, 20), mat);
  group.add(body);

  // Faint surrounding aura ring.
  const ringMat = new THREE.MeshBasicMaterial({
    color: brighten(color, 0.5),
    transparent: true,
    opacity: 0.35,
    side: THREE.DoubleSide,
  });
  const ring = new THREE.Mesh(new THREE.TorusGeometry(0.6, 0.018, 8, 48), ringMat);
  ring.rotation.x = Math.PI / 2;
  group.add(ring);

  return { group };
}

function buildSilicate(color: number, seed: number): BuiltItem {
  const group = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.05),
    roughness: 0.45,
    metalness: 0.55,
    flatShading: true,
  });

  // Fractured polyhedron — dodecahedron with bevelled edges + slight perturbation.
  const geom = new THREE.DodecahedronGeometry(0.5, 0);
  perturb(geom, 0.05, seed);
  group.add(new THREE.Mesh(geom, mat));

  const wireMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.4),
    transparent: true,
    opacity: 0.65,
  });
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(geom, 1), wireMat));
  return { group };
}

// ─── Public API ───────────────────────────────────────────────────────

/**
 * Build a 3D mesh representing the given resource tier. Geometry is
 * normalised to roughly a 1.2-unit bounding box; the caller is expected
 * to either feed it directly to {@link ./../ui/item-thumbnail.ts} or
 * scale the returned group as needed.
 */
export function buildResourceMesh(tier: ResourceTier, color: number): BuiltItem {
  const archetype = RESOURCE_ARCHETYPE[tier];
  // Stable seed per tier so a given resource always looks identical.
  const seed = (tier as number) * 0x9e3779 + 1;
  switch (archetype) {
    case 'ore': return buildOre(color, seed);
    case 'ingot': return buildIngot(color);
    case 'crystal': return buildCrystal(color);
    case 'plasma': return buildPlasma(color);
    case 'bio': return buildBio(color, seed);
    case 'cloud': return buildCloud(color, seed);
    case 'pearl': return buildPearl(color);
    case 'silicate': return buildSilicate(color, seed);
    default: return buildOre(color, seed);
  }
}

// ─── Weapon archetypes ────────────────────────────────────────────────
//
// Twenty-one weapon visual styles fall into a handful of recurring
// silhouettes: a slim energy emitter, a chunky cannon, a multi-barrel
// rotary, a missile pod, etc. Each archetype renders one shared
// silhouette and the per-weapon `projectileColor` paints it.

type WeaponArchetype =
  | 'energyBarrel'  // blaster, laser, beam, ether
  | 'cannon'        // plasma, antimatter, nova
  | 'gatling'       // gatling
  | 'needler'       // needler
  | 'coilRifle'     // rail, coil
  | 'missilePod'    // missile, torpedo, swarm, cluster
  | 'shotgun'       // shotgun
  | 'prongs'        // arc, disruptor
  | 'chrono'        // chrono
  | 'prism'         // prism
  | 'singularity'   // singularity
  | 'voidLance';    // void

const WEAPON_ARCHETYPE: Record<WeaponVisualStyle, WeaponArchetype> = {
  blaster: 'energyBarrel',
  laser: 'energyBarrel',
  beam: 'energyBarrel',
  ether: 'energyBarrel',
  plasma: 'cannon',
  antimatter: 'cannon',
  nova: 'cannon',
  gatling: 'gatling',
  needler: 'needler',
  rail: 'coilRifle',
  coil: 'coilRifle',
  missile: 'missilePod',
  torpedo: 'missilePod',
  swarm: 'missilePod',
  cluster: 'missilePod',
  shotgun: 'shotgun',
  arc: 'prongs',
  disruptor: 'prongs',
  chrono: 'chrono',
  prism: 'prism',
  singularity: 'singularity',
  void: 'voidLance',
};

function makeBarrelMaterial(): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: 0x4a5060,
    roughness: 0.4,
    metalness: 0.85,
    flatShading: true,
  });
}

function makeAccentMaterial(color: number, intensity = 1.4): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: brighten(color, 0.3),
    emissive: color,
    emissiveIntensity: intensity,
    metalness: 0.5,
    roughness: 0.25,
  });
}

function buildEnergyBarrel(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color);

  // Main barrel: slim cylinder horizontal along X.
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.14, 1.05, 12),
    barrelMat,
  );
  barrel.rotation.z = Math.PI / 2;
  group.add(barrel);

  // Receiver block at the back.
  const receiver = new THREE.Mesh(
    new THREE.BoxGeometry(0.36, 0.32, 0.32),
    barrelMat,
  );
  receiver.position.set(-0.55, 0, 0);
  group.add(receiver);

  // Emitter rings — three glowing rings near the muzzle.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.16, 0.025, 8, 18),
      accentMat,
    );
    ring.position.set(0.18 + i * 0.13, 0, 0);
    ring.rotation.y = Math.PI / 2;
    group.add(ring);
  }

  // Glowing tip core.
  const tip = new THREE.Mesh(
    new THREE.SphereGeometry(0.11, 12, 10),
    accentMat,
  );
  tip.position.set(0.6, 0, 0);
  group.add(tip);

  return { group, pulses: [accentMat] };
}

function buildCannon(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.6);

  // Chunky barrel along X.
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.28, 0.95, 14),
    barrelMat,
  );
  barrel.rotation.z = Math.PI / 2;
  group.add(barrel);

  // Heat-sink fins as torus rings.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.3, 0.045, 8, 20),
      barrelMat,
    );
    ring.position.set(-0.1 + i * 0.18, 0, 0);
    ring.rotation.y = Math.PI / 2;
    group.add(ring);
  }

  // Muzzle aperture: bright disc.
  const aperture = new THREE.Mesh(
    new THREE.CircleGeometry(0.2, 24),
    accentMat,
  );
  aperture.position.set(0.49, 0, 0);
  aperture.rotation.y = Math.PI / 2;
  group.add(aperture);

  // Rear core.
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.18, 14, 12),
    accentMat,
  );
  core.position.set(-0.55, 0, 0);
  group.add(core);

  return { group, pulses: [accentMat] };
}

function buildGatling(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.0);

  // Six small barrels arranged around a central axis.
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const b = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.07, 0.85, 8),
      barrelMat,
    );
    b.rotation.z = Math.PI / 2;
    b.position.set(0, Math.cos(a) * 0.16, Math.sin(a) * 0.16);
    group.add(b);
  }

  // Central hub disc at the back.
  const hub = new THREE.Mesh(
    new THREE.CylinderGeometry(0.26, 0.26, 0.18, 18),
    barrelMat,
  );
  hub.rotation.z = Math.PI / 2;
  hub.position.set(-0.5, 0, 0);
  group.add(hub);

  // Glowing muzzle ring.
  const ring = new THREE.Mesh(
    new THREE.TorusGeometry(0.22, 0.03, 8, 24),
    accentMat,
  );
  ring.position.set(0.45, 0, 0);
  ring.rotation.y = Math.PI / 2;
  group.add(ring);

  return { group, pulses: [accentMat] };
}

function buildNeedler(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.5);

  // 9 micro barrels in a 3x3 cluster.
  for (let r = -1; r <= 1; r++) {
    for (let c = -1; c <= 1; c++) {
      const b = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035, 0.04, 0.8, 6),
        barrelMat,
      );
      b.rotation.z = Math.PI / 2;
      b.position.set(0, r * 0.13, c * 0.13);
      group.add(b);
    }
  }

  // Backing block.
  const block = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.46, 0.46),
    barrelMat,
  );
  block.position.set(-0.5, 0, 0);
  group.add(block);

  // Glow strip on top.
  const strip = new THREE.Mesh(
    new THREE.BoxGeometry(0.55, 0.04, 0.06),
    accentMat,
  );
  strip.position.set(0, 0.22, 0);
  group.add(strip);

  return { group, pulses: [accentMat] };
}

function buildCoilRifle(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.2);

  // Long thin barrel.
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.08, 0.08, 1.15, 10),
    barrelMat,
  );
  barrel.rotation.z = Math.PI / 2;
  group.add(barrel);

  // Stock at the back.
  const stock = new THREE.Mesh(
    new THREE.BoxGeometry(0.32, 0.28, 0.28),
    barrelMat,
  );
  stock.position.set(-0.6, 0, 0);
  group.add(stock);

  // Coil rings along the barrel.
  for (let i = 0; i < 5; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.14, 0.035, 8, 20),
      accentMat,
    );
    ring.position.set(-0.3 + i * 0.18, 0, 0);
    ring.rotation.y = Math.PI / 2;
    group.add(ring);
  }

  return { group, pulses: [accentMat] };
}

function buildMissilePod(color: number): BuiltItem {
  const group = new THREE.Group();
  const podMat = new THREE.MeshStandardMaterial({
    color: 0x3a3a40,
    roughness: 0.7,
    metalness: 0.3,
    flatShading: true,
  });
  const warheadMat = makeAccentMaterial(color, 1.3);
  const stripeMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 0.7,
    metalness: 0.5,
    roughness: 0.3,
  });

  // Outer pod (rounded box).
  const pod = new THREE.Mesh(
    new THREE.BoxGeometry(0.95, 0.55, 0.55),
    podMat,
  );
  group.add(pod);

  // Tip cone — the missile's nose.
  const nose = new THREE.Mesh(
    new THREE.ConeGeometry(0.22, 0.4, 12),
    warheadMat,
  );
  nose.rotation.z = -Math.PI / 2;
  nose.position.set(0.66, 0, 0);
  group.add(nose);

  // Tail fins — four cross-shaped fins at the back.
  for (let i = 0; i < 4; i++) {
    const fin = new THREE.Mesh(
      new THREE.BoxGeometry(0.22, 0.04, 0.32),
      podMat,
    );
    fin.position.set(-0.45, 0, 0);
    fin.rotation.x = (i / 4) * Math.PI * 2;
    group.add(fin);
  }

  // Stripes painted along the pod.
  const stripe = new THREE.Mesh(
    new THREE.BoxGeometry(0.85, 0.04, 0.04),
    stripeMat,
  );
  stripe.position.set(0, 0.28, 0);
  group.add(stripe);

  return { group, pulses: [warheadMat] };
}

function buildShotgun(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.0);

  // Flared muzzle: tapered cone.
  const muzzle = new THREE.Mesh(
    new THREE.CylinderGeometry(0.32, 0.18, 0.5, 14),
    barrelMat,
  );
  muzzle.rotation.z = -Math.PI / 2;
  muzzle.position.set(0.32, 0, 0);
  group.add(muzzle);

  // Body cylinder.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.2, 0.6, 14),
    barrelMat,
  );
  body.rotation.z = Math.PI / 2;
  body.position.set(-0.22, 0, 0);
  group.add(body);

  // Pump grip.
  const grip = new THREE.Mesh(
    new THREE.BoxGeometry(0.28, 0.1, 0.24),
    barrelMat,
  );
  grip.position.set(-0.05, -0.18, 0);
  group.add(grip);

  // Muzzle disc.
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(0.28, 18),
    accentMat,
  );
  disc.position.set(0.58, 0, 0);
  disc.rotation.y = Math.PI / 2;
  group.add(disc);

  return { group, pulses: [accentMat] };
}

function buildProngs(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.6);

  // Twin prongs along X, splayed slightly outward.
  for (const sign of [-1, 1]) {
    const prong = new THREE.Mesh(
      new THREE.CylinderGeometry(0.05, 0.07, 0.85, 8),
      barrelMat,
    );
    prong.rotation.z = Math.PI / 2;
    prong.position.set(0.05, 0, sign * 0.18);
    prong.rotation.x = sign * 0.18;
    group.add(prong);

    const tip = new THREE.Mesh(
      new THREE.SphereGeometry(0.08, 12, 10),
      accentMat,
    );
    tip.position.set(0.5, 0, sign * 0.25);
    group.add(tip);
  }

  // Backing core.
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 14, 12),
    accentMat,
  );
  core.position.set(-0.45, 0, 0);
  group.add(core);

  // Arc bridge between the prong tips.
  const arcMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.6),
    transparent: true,
    opacity: 0.85,
  });
  const arcGeom = new THREE.BufferGeometry().setFromPoints([
    new THREE.Vector3(0.5, 0, -0.25),
    new THREE.Vector3(0.55, 0.08, 0),
    new THREE.Vector3(0.5, 0, 0.25),
  ]);
  group.add(new THREE.Line(arcGeom, arcMat));

  return { group, pulses: [accentMat] };
}

function buildChronoWeapon(color: number): BuiltItem {
  const group = new THREE.Group();
  const barrelMat = makeBarrelMaterial();
  const accentMat = makeAccentMaterial(color, 1.3);

  // Central barrel cylinder.
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.12, 0.85, 10),
    barrelMat,
  );
  barrel.rotation.z = Math.PI / 2;
  group.add(barrel);

  // Two large gear-like rings perpendicular to the barrel.
  for (let i = 0; i < 2; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.32, 0.05, 8, 16),
      accentMat,
    );
    ring.position.set(-0.15 + i * 0.3, 0, 0);
    ring.rotation.y = Math.PI / 2;
    group.add(ring);

    // Gear teeth as small boxes around the ring.
    for (let t = 0; t < 8; t++) {
      const a = (t / 8) * Math.PI * 2;
      const tooth = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.07, 0.04),
        barrelMat,
      );
      tooth.position.set(
        -0.15 + i * 0.3,
        Math.cos(a) * 0.36,
        Math.sin(a) * 0.36,
      );
      group.add(tooth);
    }
  }

  // Glowing hub.
  const hub = new THREE.Mesh(
    new THREE.SphereGeometry(0.13, 14, 12),
    accentMat,
  );
  hub.position.set(0, 0, 0);
  group.add(hub);

  return { group, pulses: [accentMat] };
}

function buildPrismWeapon(color: number): BuiltItem {
  const group = new THREE.Group();
  const accentMat = makeCrystalMaterial(color);
  const baseMat = makeBarrelMaterial();

  // Base mount block.
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.32, 0.32),
    baseMat,
  );
  base.position.set(-0.5, 0, 0);
  group.add(base);

  // Three crystal shards along X — each a stretched octahedron.
  const shardLengths = [0.55, 0.7, 0.55];
  const shardOffsets: [number, number, number][] = [
    [0.05, 0.16, 0],
    [0.15, 0, 0],
    [0.05, -0.16, 0],
  ];
  const wireMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.6),
    transparent: true,
    opacity: 0.7,
  });
  for (let i = 0; i < 3; i++) {
    const geom = new THREE.OctahedronGeometry(0.13, 0);
    const s = new THREE.Mesh(geom, accentMat);
    s.scale.set(shardLengths[i] / 0.13 * 0.5, 0.6, 0.6);
    s.position.set(...shardOffsets[i]);
    s.rotation.z = Math.PI / 2;
    group.add(s);
    const wire = new THREE.LineSegments(new THREE.EdgesGeometry(geom, 1), wireMat);
    wire.position.copy(s.position);
    wire.rotation.copy(s.rotation);
    wire.scale.copy(s.scale);
    group.add(wire);
  }

  return { group, pulses: [accentMat] };
}

function buildSingularityWeapon(color: number): BuiltItem {
  const group = new THREE.Group();
  const cageMat = new THREE.MeshStandardMaterial({
    color: 0x222230,
    roughness: 0.4,
    metalness: 0.85,
    flatShading: true,
  });
  // Singularity weapons are dark — the dark "pull" reads better than a
  // hot core, so the emissive is subdued and rim-tinted.
  const coreMat = new THREE.MeshStandardMaterial({
    color: darken(color, 0.5),
    emissive: color,
    emissiveIntensity: 0.9,
    metalness: 0.0,
    roughness: 0.2,
    transparent: true,
    opacity: 0.95,
  });

  // Cage: three orthogonal torus rings.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.34, 0.025, 8, 24),
      cageMat,
    );
    if (i === 0) ring.rotation.x = Math.PI / 2;
    if (i === 1) ring.rotation.y = Math.PI / 2;
    group.add(ring);
  }

  // Dark core.
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.22, 16, 14),
    coreMat,
  );
  group.add(core);

  // Receiver at the back.
  const block = new THREE.Mesh(
    new THREE.BoxGeometry(0.3, 0.3, 0.3),
    cageMat,
  );
  block.position.set(-0.55, 0, 0);
  group.add(block);

  return { group, pulses: [coreMat] };
}

function buildVoidLance(color: number): BuiltItem {
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: 0x1a1028,
    roughness: 0.5,
    metalness: 0.7,
    flatShading: true,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.4,
    metalness: 0.3,
    roughness: 0.25,
    transparent: true,
    opacity: 0.95,
  });

  // Main lance body — a long slim cylinder along X.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.16, 1.0, 12),
    shellMat,
  );
  body.rotation.z = Math.PI / 2;
  group.add(body);

  // Helical "void" strands along the body — torus knots feel right.
  const knot = new THREE.Mesh(
    new THREE.TorusKnotGeometry(0.18, 0.02, 64, 6, 3, 1),
    accentMat,
  );
  knot.scale.set(1, 0.3, 0.3);
  knot.rotation.z = Math.PI / 2;
  group.add(knot);

  // Pointed muzzle.
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(0.12, 0.3, 12),
    shellMat,
  );
  tip.rotation.z = -Math.PI / 2;
  tip.position.set(0.62, 0, 0);
  group.add(tip);

  // Glow band at the tip base.
  const band = new THREE.Mesh(
    new THREE.TorusGeometry(0.13, 0.025, 8, 18),
    accentMat,
  );
  band.position.set(0.45, 0, 0);
  band.rotation.y = Math.PI / 2;
  group.add(band);

  return { group, pulses: [accentMat] };
}

export function buildWeaponMesh(
  visualStyle: WeaponVisualStyle,
  color: number,
): BuiltItem {
  switch (WEAPON_ARCHETYPE[visualStyle]) {
    case 'energyBarrel': return buildEnergyBarrel(color);
    case 'cannon': return buildCannon(color);
    case 'gatling': return buildGatling(color);
    case 'needler': return buildNeedler(color);
    case 'coilRifle': return buildCoilRifle(color);
    case 'missilePod': return buildMissilePod(color);
    case 'shotgun': return buildShotgun(color);
    case 'prongs': return buildProngs(color);
    case 'chrono': return buildChronoWeapon(color);
    case 'prism': return buildPrismWeapon(color);
    case 'singularity': return buildSingularityWeapon(color);
    case 'voidLance': return buildVoidLance(color);
    default: return buildEnergyBarrel(color);
  }
}

// ─── Shield archetypes ────────────────────────────────────────────────

type ShieldArchetype =
  | 'dome'    // energy, phase, chrono, prism, quantum, nova
  | 'plate'   // plating, ablative, aegis
  | 'crystal' // crystal
  | 'bio'     // bioweave
  | 'voidShield'; // void

const SHIELD_ARCHETYPE: Record<ShieldVisualStyle, ShieldArchetype> = {
  energy: 'dome',
  phase: 'dome',
  chrono: 'dome',
  prism: 'dome',
  quantum: 'dome',
  nova: 'dome',
  plating: 'plate',
  ablative: 'plate',
  aegis: 'plate',
  crystal: 'crystal',
  bioweave: 'bio',
  void: 'voidShield',
};

function buildShieldDome(color: number): BuiltItem {
  const group = new THREE.Group();
  const baseMat = new THREE.MeshStandardMaterial({
    color: 0x2a2f3a,
    roughness: 0.5,
    metalness: 0.7,
    flatShading: true,
  });
  const fieldMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.2,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
    metalness: 0.0,
    roughness: 0.4,
  });

  // Emitter base — a flat puck.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.5, 0.55, 0.12, 24),
    baseMat,
  );
  base.position.set(0, -0.35, 0);
  group.add(base);

  // Three small node lights around the base.
  const nodeMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.6),
    emissive: color,
    emissiveIntensity: 1.6,
  });
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2;
    const n = new THREE.Mesh(new THREE.SphereGeometry(0.06, 10, 8), nodeMat);
    n.position.set(Math.cos(a) * 0.45, -0.28, Math.sin(a) * 0.45);
    group.add(n);
  }

  // Translucent dome above the base.
  const domeGeom = new THREE.SphereGeometry(0.55, 24, 16, 0, Math.PI * 2, 0, Math.PI / 2);
  const dome = new THREE.Mesh(domeGeom, fieldMat);
  dome.position.set(0, -0.3, 0);
  group.add(dome);

  // Hex-grid wireframe over the dome to read as "force field".
  const wireMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.6),
    transparent: true,
    opacity: 0.5,
  });
  const wire = new THREE.LineSegments(
    new THREE.EdgesGeometry(domeGeom, 30),
    wireMat,
  );
  wire.position.copy(dome.position);
  group.add(wire);

  return { group, pulses: [fieldMat, nodeMat] };
}

function buildShieldPlate(color: number): BuiltItem {
  const group = new THREE.Group();
  const plateMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.05),
    roughness: 0.45,
    metalness: 0.75,
    flatShading: true,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 0.7,
    metalness: 0.5,
    roughness: 0.3,
  });

  // Heater-shield silhouette built from extruded path.
  const shape = new THREE.Shape();
  shape.moveTo(0, 0.6);
  shape.bezierCurveTo(0.55, 0.55, 0.55, 0.05, 0.45, -0.25);
  shape.bezierCurveTo(0.3, -0.55, 0.1, -0.7, 0, -0.7);
  shape.bezierCurveTo(-0.1, -0.7, -0.3, -0.55, -0.45, -0.25);
  shape.bezierCurveTo(-0.55, 0.05, -0.55, 0.55, 0, 0.6);

  const geom = new THREE.ExtrudeGeometry(shape, {
    depth: 0.12,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.04,
    bevelThickness: 0.04,
    curveSegments: 8,
  });
  geom.center();
  geom.scale(1.0, 1.0, 1.0);
  const plate = new THREE.Mesh(geom, plateMat);
  plate.rotation.y = -0.35;
  group.add(plate);

  // Glowing cross / chevron stripe across the plate.
  const stripeV = new THREE.Mesh(
    new THREE.BoxGeometry(0.07, 0.85, 0.04),
    trimMat,
  );
  stripeV.rotation.y = -0.35;
  stripeV.position.set(0.02, 0, 0.1);
  group.add(stripeV);

  const stripeH = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.07, 0.04),
    trimMat,
  );
  stripeH.rotation.y = -0.35;
  stripeH.position.set(0.02, 0.18, 0.1);
  group.add(stripeH);

  // Center boss.
  const boss = new THREE.Mesh(
    new THREE.SphereGeometry(0.1, 14, 12),
    trimMat,
  );
  boss.position.set(0.02, 0.18, 0.18);
  group.add(boss);

  return { group, pulses: [trimMat] };
}

function buildShieldCrystal(color: number): BuiltItem {
  const group = new THREE.Group();
  const mat = makeCrystalMaterial(color);
  const wireMat = new THREE.LineBasicMaterial({
    color: brighten(color, 0.6),
    transparent: true,
    opacity: 0.8,
  });

  // Central tall crystal.
  const big = new THREE.Mesh(new THREE.OctahedronGeometry(0.32, 0), mat);
  big.scale.set(0.55, 1.2, 0.55);
  group.add(big);
  group.add(new THREE.LineSegments(new THREE.EdgesGeometry(big.geometry, 1), wireMat));

  // Two flanking shards.
  for (const sign of [-1, 1]) {
    const s = new THREE.Mesh(new THREE.OctahedronGeometry(0.2, 0), mat);
    s.scale.set(0.45, 0.85, 0.45);
    s.position.set(sign * 0.28, -0.05, 0);
    s.rotation.z = sign * 0.25;
    group.add(s);
  }

  // Base puck.
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.4, 0.45, 0.08, 18),
    new THREE.MeshStandardMaterial({
      color: 0x2a2a35,
      roughness: 0.6,
      metalness: 0.5,
    }),
  );
  base.position.set(0, -0.42, 0);
  group.add(base);

  return { group, pulses: [mat] };
}

function buildShieldBio(color: number): BuiltItem {
  const group = new THREE.Group();
  const meshMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.05),
    roughness: 0.7,
    metalness: 0.1,
    emissive: color,
    emissiveIntensity: 0.3,
    flatShading: true,
  });
  const veinMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: color,
    emissiveIntensity: 1.4,
    roughness: 0.3,
    metalness: 0.1,
  });

  // Lumpy oblate body — like a seed pod.
  const bodyGeom = new THREE.SphereGeometry(0.5, 14, 12);
  perturb(bodyGeom, 0.06, 17);
  const body = new THREE.Mesh(bodyGeom, meshMat);
  body.scale.set(1.0, 1.15, 0.9);
  group.add(body);

  // Pulsing vein circles wrapping the body.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.5 - i * 0.05, 0.025, 8, 24),
      veinMat,
    );
    ring.rotation.x = Math.PI / 2 + i * 0.3;
    ring.scale.set(1, 1.1, 1);
    group.add(ring);
  }

  return { group, pulses: [veinMat] };
}

function buildShieldVoid(color: number): BuiltItem {
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: 0x0a0518,
    roughness: 0.4,
    metalness: 0.95,
    flatShading: true,
  });
  const fieldMat = new THREE.MeshStandardMaterial({
    color: darken(color, 0.3),
    emissive: color,
    emissiveIntensity: 1.0,
    transparent: true,
    opacity: 0.55,
    side: THREE.DoubleSide,
  });

  // Spiked icosahedron cage.
  const cageGeom = new THREE.IcosahedronGeometry(0.55, 0);
  const cage = new THREE.Mesh(cageGeom, shellMat);
  group.add(cage);
  group.add(new THREE.LineSegments(
    new THREE.EdgesGeometry(cageGeom, 1),
    new THREE.LineBasicMaterial({
      color: brighten(color, 0.5),
      transparent: true,
      opacity: 0.7,
    }),
  ));

  // Inner field core (smaller dark sphere).
  const core = new THREE.Mesh(
    new THREE.SphereGeometry(0.32, 16, 12),
    fieldMat,
  );
  group.add(core);

  return { group, pulses: [fieldMat] };
}

export function buildShieldMesh(
  visualStyle: ShieldVisualStyle,
  color: number,
): BuiltItem {
  switch (SHIELD_ARCHETYPE[visualStyle]) {
    case 'dome': return buildShieldDome(color);
    case 'plate': return buildShieldPlate(color);
    case 'crystal': return buildShieldCrystal(color);
    case 'bio': return buildShieldBio(color);
    case 'voidShield': return buildShieldVoid(color);
    default: return buildShieldDome(color);
  }
}

// ─── Engine archetypes ────────────────────────────────────────────────

type EngineArchetype =
  | 'thruster' // basic_thruster
  | 'ion'      // ion_drive
  | 'plasma'   // plasma_drive
  | 'warp'     // warp_engine
  | 'bloom'    // sylphari_bloom_drive
  | 'chronoEng'; // chronos_temporal_drive

const ENGINE_ARCHETYPE: Record<string, EngineArchetype> = {
  basic_thruster: 'thruster',
  ion_drive: 'ion',
  plasma_drive: 'plasma',
  warp_engine: 'warp',
  sylphari_bloom_drive: 'bloom',
  chronos_temporal_drive: 'chronoEng',
};

const ENGINE_COLOR: Record<string, number> = {
  basic_thruster: 0xff8844,
  ion_drive: 0x88ccff,
  plasma_drive: 0xcc66ff,
  warp_engine: 0x66ffee,
  sylphari_bloom_drive: 0x88ffaa,
  chronos_temporal_drive: 0xbbeeff,
};

function buildSimpleNozzle(
  color: number,
  outerRadius: number,
  innerRadius: number,
  length: number,
  emissiveBoost = 1.2,
): BuiltItem {
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: 0x404048,
    roughness: 0.4,
    metalness: 0.85,
    flatShading: true,
  });
  const innerMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: emissiveBoost,
    transparent: true,
    opacity: 0.95,
    side: THREE.DoubleSide,
  });

  // Tapered nozzle (open cone — outer shell).
  const nozzle = new THREE.Mesh(
    new THREE.CylinderGeometry(outerRadius, innerRadius, length, 24, 1, true),
    shellMat,
  );
  nozzle.rotation.z = Math.PI / 2;
  group.add(nozzle);

  // Inner glow — slightly smaller cylinder.
  const inner = new THREE.Mesh(
    new THREE.CylinderGeometry(outerRadius * 0.8, innerRadius * 0.7, length * 0.95, 20, 1, true),
    innerMat,
  );
  inner.rotation.z = Math.PI / 2;
  group.add(inner);

  // Glowing core puck at the throat.
  const core = new THREE.Mesh(
    new THREE.CircleGeometry(innerRadius * 0.7, 18),
    innerMat,
  );
  core.position.set(-length * 0.45, 0, 0);
  core.rotation.y = -Math.PI / 2;
  group.add(core);

  return { group, pulses: [innerMat] };
}

function buildThruster(color: number): BuiltItem {
  return buildSimpleNozzle(color, 0.36, 0.22, 0.85, 1.0);
}

function buildIonDrive(color: number): BuiltItem {
  const built = buildSimpleNozzle(color, 0.3, 0.18, 0.95, 1.4);
  // Add three thin tail rings that read as "ion blue".
  const ringMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.6),
    emissive: color,
    emissiveIntensity: 1.2,
    metalness: 0.3,
    roughness: 0.3,
  });
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.32 - i * 0.04, 0.02, 8, 18),
      ringMat,
    );
    ring.position.set(0.3 + i * 0.12, 0, 0);
    ring.rotation.y = Math.PI / 2;
    built.group.add(ring);
  }
  built.pulses = [...(built.pulses ?? []), ringMat];
  return built;
}

function buildPlasmaDrive(color: number): BuiltItem {
  const built = buildSimpleNozzle(color, 0.42, 0.24, 0.85, 1.6);
  // Plasma core sphere visible through the nozzle.
  const coreMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.8,
    transparent: true,
    opacity: 0.92,
  });
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.18, 16, 12), coreMat);
  core.position.set(-0.15, 0, 0);
  built.group.add(core);
  built.pulses = [...(built.pulses ?? []), coreMat];
  return built;
}

function buildWarpEngine(color: number): BuiltItem {
  const group = new THREE.Group();
  const hullMat = new THREE.MeshStandardMaterial({
    color: 0x303040,
    roughness: 0.4,
    metalness: 0.85,
    flatShading: true,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.6,
    metalness: 0.3,
    roughness: 0.25,
    transparent: true,
    opacity: 0.95,
  });

  // Twin warp nacelles along X, side by side along Z.
  for (const sign of [-1, 1]) {
    const nac = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.18, 0.95, 16),
      hullMat,
    );
    nac.rotation.z = Math.PI / 2;
    nac.position.set(0, 0, sign * 0.26);
    group.add(nac);

    const cap = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 14, 10),
      accentMat,
    );
    cap.position.set(0.45, 0, sign * 0.26);
    group.add(cap);

    const tail = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 14, 10),
      hullMat,
    );
    tail.position.set(-0.5, 0, sign * 0.26);
    group.add(tail);
  }

  // Central pylon connecting the two nacelles.
  const pylon = new THREE.Mesh(
    new THREE.BoxGeometry(0.6, 0.12, 0.6),
    hullMat,
  );
  pylon.position.set(-0.05, -0.05, 0);
  group.add(pylon);

  return { group, pulses: [accentMat] };
}

function buildBloomEngine(color: number): BuiltItem {
  const group = new THREE.Group();
  const petalMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.1),
    roughness: 0.5,
    metalness: 0.2,
    emissive: color,
    emissiveIntensity: 0.4,
    flatShading: true,
  });
  const coreMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: color,
    emissiveIntensity: 1.6,
  });

  // Six "petals" forming a flower-like nozzle.
  for (let i = 0; i < 6; i++) {
    const petal = new THREE.Mesh(
      new THREE.ConeGeometry(0.16, 0.55, 6),
      petalMat,
    );
    petal.position.set(0, 0, 0);
    const a = (i / 6) * Math.PI * 2;
    petal.rotation.x = a;
    petal.rotation.z = -Math.PI / 2 + 0.55;
    petal.position.set(0.15, Math.cos(a) * 0.25, Math.sin(a) * 0.25);
    group.add(petal);
  }

  // Stem / mount at the back.
  const stem = new THREE.Mesh(
    new THREE.CylinderGeometry(0.12, 0.16, 0.45, 12),
    petalMat,
  );
  stem.rotation.z = Math.PI / 2;
  stem.position.set(-0.45, 0, 0);
  group.add(stem);

  // Bright core at the center of the petals.
  const core = new THREE.Mesh(new THREE.SphereGeometry(0.13, 14, 12), coreMat);
  core.position.set(0.1, 0, 0);
  group.add(core);

  return { group, pulses: [coreMat] };
}

function buildChronoEngine(color: number): BuiltItem {
  const built = buildSimpleNozzle(color, 0.32, 0.2, 0.7, 1.2);
  // Two large clock-gear rings around the nozzle.
  const gearMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: color,
    emissiveIntensity: 1.0,
    metalness: 0.4,
    roughness: 0.3,
  });
  const toothMat = new THREE.MeshStandardMaterial({
    color: 0x404048,
    roughness: 0.4,
    metalness: 0.85,
  });
  for (let i = 0; i < 2; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.4, 0.04, 8, 18),
      gearMat,
    );
    ring.position.set(-0.05 + i * 0.25, 0, 0);
    ring.rotation.y = Math.PI / 2;
    built.group.add(ring);
    for (let t = 0; t < 8; t++) {
      const a = (t / 8) * Math.PI * 2;
      const tooth = new THREE.Mesh(
        new THREE.BoxGeometry(0.04, 0.07, 0.04),
        toothMat,
      );
      tooth.position.set(
        -0.05 + i * 0.25,
        Math.cos(a) * 0.46,
        Math.sin(a) * 0.46,
      );
      built.group.add(tooth);
    }
  }
  built.pulses = [...(built.pulses ?? []), gearMat];
  return built;
}

export function buildEngineMesh(engineId: string, colorOverride?: number): BuiltItem {
  const archetype = ENGINE_ARCHETYPE[engineId] ?? 'thruster';
  const color = colorOverride ?? ENGINE_COLOR[engineId] ?? 0x88ccff;
  switch (archetype) {
    case 'thruster': return buildThruster(color);
    case 'ion': return buildIonDrive(color);
    case 'plasma': return buildPlasmaDrive(color);
    case 'warp': return buildWarpEngine(color);
    case 'bloom': return buildBloomEngine(color);
    case 'chronoEng': return buildChronoEngine(color);
    default: return buildThruster(color);
  }
}

// ─── Consumable archetypes ────────────────────────────────────────────

export type ConsumableEffectKind =
  | 'repairHull'
  | 'repairShield'
  | 'boostThrust'
  | 'boostDamage';

const CONSUMABLE_COLOR: Record<ConsumableEffectKind, number> = {
  repairHull: 0xff8844,
  repairShield: 0x44ccff,
  boostThrust: 0xffcc44,
  boostDamage: 0xff4488,
};

function buildVialBase(color: number, capColor = 0x444450): {
  group: THREE.Group;
  liquidMat: THREE.MeshStandardMaterial;
} {
  const group = new THREE.Group();
  const glassMat = new THREE.MeshStandardMaterial({
    color: 0x88aabb,
    transparent: true,
    opacity: 0.35,
    metalness: 0.1,
    roughness: 0.1,
  });
  const liquidMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.3),
    emissive: color,
    emissiveIntensity: 1.2,
    transparent: true,
    opacity: 0.95,
  });
  const capMat = new THREE.MeshStandardMaterial({
    color: capColor,
    metalness: 0.7,
    roughness: 0.4,
    flatShading: true,
  });

  // Main glass cylinder — vertical along Y.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.22, 0.22, 0.85, 18),
    glassMat,
  );
  group.add(body);

  // Liquid (slightly smaller) inside the body.
  const liquid = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.62, 18),
    liquidMat,
  );
  liquid.position.y = -0.08;
  group.add(liquid);

  // Cap.
  const cap = new THREE.Mesh(
    new THREE.CylinderGeometry(0.24, 0.22, 0.18, 18),
    capMat,
  );
  cap.position.y = 0.5;
  group.add(cap);

  // Base ring.
  const baseRing = new THREE.Mesh(
    new THREE.CylinderGeometry(0.24, 0.24, 0.06, 18),
    capMat,
  );
  baseRing.position.y = -0.45;
  group.add(baseRing);

  return { group, liquidMat };
}

function buildHullKit(color: number): BuiltItem {
  // A boxy field-kit with a wrench/cross icon — repairs hull.
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.05),
    roughness: 0.45,
    metalness: 0.4,
    flatShading: true,
  });
  const trimMat = new THREE.MeshStandardMaterial({
    color: 0xeeeeee,
    emissive: 0xffffff,
    emissiveIntensity: 0.3,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.2,
  });

  // Toolbox body.
  const body = new THREE.Mesh(
    new THREE.BoxGeometry(0.85, 0.55, 0.55),
    shellMat,
  );
  group.add(body);

  // Lid lip.
  const lid = new THREE.Mesh(
    new THREE.BoxGeometry(0.88, 0.06, 0.58),
    new THREE.MeshStandardMaterial({
      color: darken(color, 0.3),
      roughness: 0.5,
      metalness: 0.5,
    }),
  );
  lid.position.y = 0.28;
  group.add(lid);

  // White cross on the front (medkit style).
  const crossV = new THREE.Mesh(
    new THREE.BoxGeometry(0.1, 0.36, 0.04),
    trimMat,
  );
  crossV.position.set(0, 0, 0.29);
  group.add(crossV);
  const crossH = new THREE.Mesh(
    new THREE.BoxGeometry(0.36, 0.1, 0.04),
    trimMat,
  );
  crossH.position.set(0, 0, 0.29);
  group.add(crossH);

  // Status LED.
  const led = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 10, 8),
    accentMat,
  );
  led.position.set(0.3, 0.18, 0.28);
  group.add(led);

  return { group, pulses: [accentMat] };
}

function buildShieldBatteryItem(color: number): BuiltItem {
  // A battery cell — vertical cylinder with terminals.
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: 0x303a48,
    roughness: 0.4,
    metalness: 0.7,
    flatShading: true,
  });
  const stripeMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 1.4,
    metalness: 0.4,
    roughness: 0.3,
  });
  const terminalMat = new THREE.MeshStandardMaterial({
    color: 0xc0c0c8,
    metalness: 0.8,
    roughness: 0.3,
  });

  // Cell body.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.3, 0.3, 0.85, 22),
    shellMat,
  );
  group.add(body);

  // Charge stripes — three glowing rings near the top.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.31, 0.02, 8, 22),
      stripeMat,
    );
    ring.position.y = 0.02 + i * 0.12;
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
  }

  // Top terminal nub.
  const term = new THREE.Mesh(
    new THREE.CylinderGeometry(0.1, 0.12, 0.14, 14),
    terminalMat,
  );
  term.position.y = 0.5;
  group.add(term);

  // Bottom flange.
  const flange = new THREE.Mesh(
    new THREE.CylinderGeometry(0.32, 0.32, 0.06, 22),
    terminalMat,
  );
  flange.position.y = -0.45;
  group.add(flange);

  // Lightning bolt accent on the side as a thin emissive box.
  const bolt = new THREE.Mesh(
    new THREE.BoxGeometry(0.04, 0.35, 0.04),
    new THREE.MeshStandardMaterial({
      color: 0xffffff,
      emissive: 0xffffff,
      emissiveIntensity: 0.8,
    }),
  );
  bolt.position.set(0, 0, 0.31);
  bolt.rotation.z = 0.4;
  group.add(bolt);

  return { group, pulses: [stripeMat] };
}

function buildBoostThrust(color: number): BuiltItem {
  // Vial with a small fin/arrow accent.
  const { group, liquidMat } = buildVialBase(color, 0x886633);

  // Two stylised fins on either side of the cap to read as "thrust".
  const finMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.4),
    emissive: color,
    emissiveIntensity: 0.9,
    metalness: 0.3,
    roughness: 0.4,
    flatShading: true,
  });
  for (const sign of [-1, 1]) {
    const fin = new THREE.Mesh(
      new THREE.ConeGeometry(0.1, 0.2, 4),
      finMat,
    );
    fin.position.set(sign * 0.28, 0.3, 0);
    fin.rotation.z = sign * Math.PI * 0.55;
    group.add(fin);
  }

  // Glow trail underneath suggesting exhaust.
  const trailMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: color,
    emissiveIntensity: 1.4,
    transparent: true,
    opacity: 0.7,
  });
  const trail = new THREE.Mesh(
    new THREE.ConeGeometry(0.14, 0.32, 12),
    trailMat,
  );
  trail.position.y = -0.65;
  trail.rotation.z = Math.PI;
  group.add(trail);

  return { group, pulses: [liquidMat, trailMat] };
}

function buildBoostDamage(color: number): BuiltItem {
  // Stim cartridge: cylinder with dart-like tip and red banding.
  const group = new THREE.Group();
  const shellMat = new THREE.MeshStandardMaterial({
    color: 0xb8b8c0,
    metalness: 0.7,
    roughness: 0.35,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.3),
    emissive: color,
    emissiveIntensity: 1.4,
    metalness: 0.4,
    roughness: 0.3,
  });
  const liquidMat = new THREE.MeshStandardMaterial({
    color: brighten(color, 0.5),
    emissive: color,
    emissiveIntensity: 1.0,
    transparent: true,
    opacity: 0.9,
  });

  // Cartridge body.
  const body = new THREE.Mesh(
    new THREE.CylinderGeometry(0.18, 0.18, 0.7, 18),
    shellMat,
  );
  group.add(body);

  // Window section showing the liquid (thin cylinder).
  const window = new THREE.Mesh(
    new THREE.CylinderGeometry(0.181, 0.181, 0.32, 18),
    liquidMat,
  );
  window.position.y = -0.05;
  group.add(window);

  // Tapered injector tip (cone).
  const tip = new THREE.Mesh(
    new THREE.ConeGeometry(0.08, 0.32, 14),
    shellMat,
  );
  tip.position.y = 0.5;
  group.add(tip);

  // Three red bands around the body.
  for (let i = 0; i < 3; i++) {
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.19, 0.02, 8, 22),
      accentMat,
    );
    ring.position.y = -0.18 + i * 0.18;
    ring.rotation.x = Math.PI / 2;
    group.add(ring);
  }

  // Plunger ring at the bottom.
  const plunger = new THREE.Mesh(
    new THREE.CylinderGeometry(0.2, 0.2, 0.06, 18),
    shellMat,
  );
  plunger.position.y = -0.4;
  group.add(plunger);

  return { group, pulses: [accentMat, liquidMat] };
}

export function buildConsumableMesh(
  effectKind: ConsumableEffectKind,
  colorOverride?: number,
): BuiltItem {
  const color = colorOverride ?? CONSUMABLE_COLOR[effectKind];
  switch (effectKind) {
    case 'repairHull': return buildHullKit(color);
    case 'repairShield': return buildShieldBatteryItem(color);
    case 'boostThrust': return buildBoostThrust(color);
    case 'boostDamage': return buildBoostDamage(color);
    default: return buildHullKit(color);
  }
}

/**
 * Free GPU resources owned by a built item (geometries + materials).
 * Called by the thumbnail renderer when it cycles to a new item.
 */
export function disposeBuiltItem(item: BuiltItem): void {
  item.group.traverse((o) => {
    const mesh = o as THREE.Mesh | THREE.LineSegments;
    if ((mesh as THREE.Mesh).geometry) {
      (mesh as THREE.Mesh).geometry.dispose();
    }
    const m = (mesh as THREE.Mesh).material as
      | THREE.Material
      | THREE.Material[]
      | undefined;
    if (Array.isArray(m)) m.forEach((mat) => mat.dispose());
    else if (m) m.dispose();
  });
}
